import React, { useContext, useState } from 'react'
import LocalErrorBoundary from '../../components/LocalErrorBoundary'
import { Accordion, PageWrapper } from '../../components'
import { WemoloButton, WemoloToggle, WemoloTypography } from '../../components/Wemolo'
import { MatchingFlagContext } from './MatchingFlagContext'
import { LotList } from './LotList'
import styles from './MatchingFlagUI.module.css'

export const MatchingFlagUI = () => {
    const matchingFlagContext = useContext(MatchingFlagContext)
    const [inputValue, setInputValue] = useState('')

    const handleSave = () => {
        matchingFlagContext.handleSubmit()
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setInputValue(value)
    }

    const handleInputBlur = () => {
        if (inputValue.trim()) {
            const newLots = inputValue
                .trim()
                .split(/\s+/)
                .filter((lot) => lot.length > 0)
                .filter((lot) => !matchingFlagContext.excludedLots.includes(lot))

            matchingFlagContext.setExcludedLots([...matchingFlagContext.excludedLots, ...newLots])
            setInputValue('')
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleInputBlur()
        }
    }

    const removeLot = (lotToRemove: string) => {
        matchingFlagContext.setExcludedLots(matchingFlagContext.excludedLots.filter((lot) => lot !== lotToRemove))
    }

    return (
        <LocalErrorBoundary tag="Matching Flag">
            <PageWrapper>
                <Accordion initialExpandState={true} isLoading={matchingFlagContext.loading}>
                    <div id="matching-header" className={styles.header}>
                        <div className={styles.headerLeft}>
                            <WemoloTypography variant="title-medium" tag="h5" className={styles.title}>
                                Pause matching across all lots
                            </WemoloTypography>
                            <WemoloToggle
                                size="small"
                                checked={matchingFlagContext.toggle}
                                onChange={(e) => matchingFlagContext.setToggle(e.target.checked)}
                            />
                        </div>

                        <WemoloButton
                            view="primary"
                            onClick={handleSave}
                            disabled={!matchingFlagContext.hasChanges || matchingFlagContext.saving}
                            loading={matchingFlagContext.saving}
                        >
                            Save Configuration
                        </WemoloButton>
                    </div>
                    {matchingFlagContext.toggle && (
                        <div className={styles.body}>
                            <div className={styles.inputBlock}>
                                <label htmlFor="excluded-lots" className={styles.inputLabel}>
                                    <WemoloTypography variant="body-medium-emphasized" tag="span">
                                        Paste lot IDs that will be excluded from temporary pause matching and press
                                        ENTER
                                    </WemoloTypography>
                                </label>
                                <textarea
                                    id="excluded-lots"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onBlur={handleInputBlur}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Paste lot IDs (e.g., 001-123 002-122 001-111)"
                                    className={styles.textarea}
                                />
                            </div>

                            <LotList
                                label="Resulting list of excluded lots"
                                lots={matchingFlagContext.excludedLots}
                                onRemove={removeLot}
                                emptyMessage="No excluded lots"
                                rightAction={
                                    <button
                                        onClick={() => matchingFlagContext.setExcludedLots([])}
                                        disabled={matchingFlagContext.excludedLots.length === 0}
                                        className={styles.clearAllButton}
                                    >
                                        <WemoloTypography variant="label-xsmall" tag="span">
                                            Clear All
                                        </WemoloTypography>
                                    </button>
                                }
                            />

                            {matchingFlagContext.invalidLots.length > 0 && (
                                <LotList
                                    label="Invalid lots (not found in database)"
                                    lots={matchingFlagContext.invalidLots}
                                    variant="error"
                                    showRemoveButton={false}
                                    rightAction={
                                        <button
                                            onClick={matchingFlagContext.clearInvalidLots}
                                            className={styles.dismissButton}
                                            aria-label="Dismiss invalid lots"
                                        >
                                            ×
                                        </button>
                                    }
                                />
                            )}
                        </div>
                    )}
                </Accordion>
            </PageWrapper>
        </LocalErrorBoundary>
    )
}
